/**
 * API Status Component
 *
 * Displays backend API connection status with URL and health information.
 * Shows connection indicator, response time, and allows manual refresh.
 *
 * Usage:
 *   <ApiStatus apiUrl="http://localhost:8000" />
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Card,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';

interface HealthCheckResult {
  connected: boolean;
  status?: number;
  responseTime?: number;
  error?: string;
  version?: string;
}

interface ApiStatusProps {
  /** Backend API URL to check */
  apiUrl: string;
  /** Optional auto-refresh interval in milliseconds */
  autoRefreshMs?: number;
  /** Whether to show in compact mode */
  compact?: boolean;
}

/**
 * Check API health endpoint
 */
async function checkApiHealth(apiUrl: string, timeout = 5000): Promise<HealthCheckResult> {
  const startTime = performance.now();

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const response = await fetch(`${apiUrl}/healthz`, {
      method: 'GET',
      signal: controller.signal,
      headers: { 'Accept': 'application/json' }
    });

    clearTimeout(timeoutId);
    const responseTime = Math.round(performance.now() - startTime);

    let data: unknown = null;
    try {
      data = await response.json();
    } catch {
      // Ignore JSON parse errors
    }

    return {
      connected: response.ok,
      status: response.status,
      responseTime,
      version: (data as { version?: string; apiVersion?: string })?.version ||
               (data as { version?: string; apiVersion?: string })?.apiVersion || undefined
    };
  } catch (error) {
    const responseTime = Math.round(performance.now() - startTime);
    let errorMessage = 'Unknown error';

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        errorMessage = `Timeout after ${timeout}ms`;
      } else {
        errorMessage = error.message;
      }
    }

    return {
      connected: false,
      responseTime,
      error: errorMessage
    };
  }
}

/**
 * API Status display component
 *
 * Shows connection status, API URL, response time, and manual refresh button.
 */
export function ApiStatus({ apiUrl, autoRefreshMs, compact = false }: ApiStatusProps): JSX.Element {
  const [health, setHealth] = useState<HealthCheckResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const { theme } = useTheme();

  // Theme-aware colors from design system
  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const successColor = theme.colors.semantic.success.default;
  const errorColor = theme.colors.semantic.error.default;

  const checkHealth = useCallback(async () => {
    setLoading(true);
    const result = await checkApiHealth(apiUrl);
    setHealth(result);
    setLoading(false);
    setLastChecked(new Date());
  }, [apiUrl]);

  // Check health on mount
  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  // Optional auto-refresh
  useEffect(() => {
    if (!autoRefreshMs) return;

    const interval = setInterval(checkHealth, autoRefreshMs);
    return () => clearInterval(interval);
  }, [checkHealth, autoRefreshMs]);

  // Status indicator color
  const getStatusColor = () => {
    if (loading) return mutedColor;
    if (!health) return mutedColor;
    return health.connected ? successColor : errorColor;
  };

  // Status text
  const getStatusText = () => {
    if (loading) return 'Checking...';
    if (!health) return 'Unknown';
    if (health.connected) {
      return `Connected ${health.responseTime ? `(${health.responseTime}ms)` : ''}`;
    }
    return `Disconnected`;
  };

  // Compact mode - single line display
  if (compact) {
    return (
      <Flex align="center" gap="sm" style={{ fontSize: '0.85rem', color: mutedColor }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: getStatusColor(),
          flexShrink: 0
        }} />
        <span style={{ color: getStatusColor() }}>
          API: {getStatusText()}
        </span>
        {!loading && (
          <Button
            variant="ghost"
            size="sm"
            onClick={checkHealth}
            title="Refresh API status"
            style={{ padding: '0.15rem 0.4rem', fontSize: '0.75rem' }}
          >
            ↻
          </Button>
        )}
      </Flex>
    );
  }

  // Full mode - detailed display
  return (
    <Card variant="outlined" padding="sm">
      <Flex justify="between" align="center" style={{ marginBottom: spacing.sm }}>
        <Flex align="center" gap="sm">
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: getStatusColor(),
            flexShrink: 0
          }} />
          <span style={{ fontWeight: 600, color: textColor }}>
            Backend API
          </span>
        </Flex>
        <Button
          variant="ghost"
          size="sm"
          onClick={checkHealth}
          disabled={loading}
        >
          {loading ? 'Checking...' : '↻ Refresh'}
        </Button>
      </Flex>

      <dl style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: `${spacing.xs} ${spacing.sm}`,
        margin: 0,
        fontSize: '0.8rem'
      }}>
        <dt style={{ color: mutedColor }}>URL</dt>
        <dd style={{
          margin: 0,
          color: textColor,
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          wordBreak: 'break-all'
        }}>
          {apiUrl}
        </dd>

        <dt style={{ color: mutedColor }}>Status</dt>
        <dd style={{ margin: 0, color: getStatusColor() }}>
          {getStatusText()}
        </dd>

        {health?.status && (
          <>
            <dt style={{ color: mutedColor }}>HTTP</dt>
            <dd style={{ margin: 0, color: textColor }}>
              {health.status}
            </dd>
          </>
        )}

        {health?.version && (
          <>
            <dt style={{ color: mutedColor }}>Version</dt>
            <dd style={{ margin: 0, color: textColor }}>
              {health.version}
            </dd>
          </>
        )}

        {health?.error && (
          <>
            <dt style={{ color: mutedColor }}>Error</dt>
            <dd style={{ margin: 0, color: errorColor, fontSize: '0.75rem' }}>
              {health.error}
              {/* Provide helpful hint for local API connection failures */}
              {apiUrl.includes('localhost') && health.error.includes('Failed to fetch') && (
                <div style={{ marginTop: spacing.xs, color: mutedColor, fontStyle: 'italic' }}>
                  Hint: Is the local backend running? Try: docker compose up backend
                </div>
              )}
            </dd>
          </>
        )}

        {lastChecked && (
          <>
            <dt style={{ color: mutedColor }}>Checked</dt>
            <dd style={{ margin: 0, color: mutedColor, fontSize: '0.75rem' }}>
              {lastChecked.toLocaleTimeString()}
            </dd>
          </>
        )}
      </dl>
    </Card>
  );
}
